import { useEffect, useRef } from 'react'
import type { DestinationEntry, DestinationTab } from './tabs'

/**
 * The tabbed "Find Your Destination" panel (#1072 D1, toolkit
 * destination-filter). Rendered INSIDE the Where field's popover shell, so
 * popover positioning, portaling and the mobile sheet keep working unchanged —
 * this component owns the tabs, the accordion and the rows, and nothing else.
 *
 * Deliberately CONTROLLED and DUMB: the active tab, the expanded islands and
 * the keyboard cursor all live in ProWhereField, which also owns the field's
 * input, its Enter/Arrow handling and the flat item list. The panel numbers its
 * rows by walking groups → entries → children, the same order
 * `flattenTabEntries` produces under the SAME `isExpanded` gate, so
 * `activeIndex` here and `flatItems[activeIndex]` there are the same row.
 *
 * Search is not re-implemented here: the field already has an input, and the
 * caller passes tabs already narrowed through filterDestinationTabs. A second
 * search box inside the popup would give the field two competing queries.
 *
 * Rows commit on click; mousedown only preventDefaults, like the field's plain
 * suggestion rows: the input keeps focus, so a tab switch never blurs it and
 * the visitor can keep typing to narrow the list.
 *
 * ARIA (codex/#1246 review): the tabs are NOT allowed inside a listbox, which
 * may only own options and groups. The Where field therefore drops its own
 * `role="listbox"` in destination mode and the listbox lives HERE, on the row
 * list, with the tablist as its sibling.
 */
export default function DestinationPanel({
  title,
  tabs,
  activeKey,
  onTabChange,
  activeIndex,
  showCounts,
  onSelect,
  isExpanded,
  onToggleExpand,
  optionId,
}: {
  title: string
  tabs: DestinationTab[]
  activeKey: string | undefined
  onTabChange: (key: string) => void
  activeIndex: number
  showCounts: boolean
  onSelect: (entry: DestinationEntry) => void
  isExpanded: (entry: DestinationEntry) => boolean
  onToggleExpand: (entry: DestinationEntry) => void
  optionId: (index: number) => string
}) {
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0]
  // The active row can sit far below the fold of the 420px popover, and an
  // accordion makes the list longer still — an arrow key that highlights an
  // invisible row reads as "the keyboard stopped working".
  const activeRowRef = useRef<HTMLButtonElement | null>(null)
  useEffect(() => {
    // optional call: jsdom has no scrollIntoView, and the render tests mount
    // this panel for real.
    activeRowRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex])

  if (!active) return null

  let index = -1
  const renderEntry = (entry: DestinationEntry, depth: number) => {
    index++
    const idx = index
    const expandable = entry.children.length > 0
    const expanded = expandable && isExpanded(entry)
    return (
      <div key={`${entry.filterKey}-${entry.id}`} className="hfse-dest-entry" role="presentation">
        <div className="hfse-dest-row" role="presentation">
          <button
            type="button"
            role="option"
            id={optionId(idx)}
            ref={idx === activeIndex ? activeRowRef : undefined}
            aria-selected={idx === activeIndex}
            className={`hfse-dest-item ${depth > 0 ? 'hfse-dest-item-child' : ''} ${idx === activeIndex ? 'hfse-dest-item-active' : ''}`}
            // mousedown only guards focus (the input must not blur-flicker, like
            // the plain rows); the ACTION rides on click so a keyboard user who
            // tabbed to the row gets it from Enter/Space too.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(entry)}
          >
            <span className="hfse-dest-item-label">{entry.label}</span>
            {showCounts && entry.count > 0 && <span className="hfse-dest-item-count">{entry.count}</span>}
          </button>
          {/* The disclosure is its OWN control, not the row: clicking an island
              has always applied that island's filter, and overloading the same
              hit area with expand/collapse would take that away. */}
          {expandable && (
            <button
              type="button"
              className="hfse-dest-toggle"
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${entry.label}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onToggleExpand(entry)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>
        {expanded && (
          <div className="hfse-dest-children" role="presentation">
            {entry.children.map((child) => renderEntry(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="hfse-dest-panel">
      {title && <div className="hfse-dest-title">{title}</div>}

      {tabs.length > 1 && (
        <div
          className="hfse-dest-tabs"
          role="tablist"
          // ARIA tabs pattern, minimal form: Left/Right move the active tab.
          // (Full roving tabindex is a follow-on; the tabs are Tab-reachable
          // buttons and Enter/Space activate via click.)
          onKeyDown={(e) => {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
            e.preventDefault()
            const idx = tabs.findIndex((t) => t.key === active.key)
            const next = tabs[(idx + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length]
            if (next) onTabChange(next.key)
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tab.key === active.key}
              className={`hfse-dest-tab ${tab.key === active.key ? 'hfse-dest-tab-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      <div className="hfse-dest-list" role="listbox" aria-label={active.label}>
        {active.groups.map((group) => (
          <div
            key={group.key}
            className="hfse-dest-group"
            role={group.label ? 'group' : 'presentation'}
            aria-label={group.label ?? undefined}
          >
            {group.label && <div className="hfse-dest-group-heading">{group.label}</div>}
            {group.entries.map((entry) => renderEntry(entry, 0))}
          </div>
        ))}
      </div>
    </div>
  )
}
